import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";

function Stars({ value, onChange, size = "h-5 w-5" }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          onMouseEnter={() => onChange && setHover(n)}
          onMouseLeave={() => onChange && setHover(0)}
          onClick={() => onChange && onChange(n)}
          className={onChange ? "cursor-pointer" : "cursor-default"}
          data-testid={onChange ? `review-star-${n}` : undefined}
        >
          <Star className={`${size} ${n <= (hover || value) ? "fill-primary text-primary" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews({ businessId }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/businesses/${businessId}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [businessId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error(lang === "es" ? "Elige una calificación" : "Pick a rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/businesses/${businessId}/reviews`, { rating, comment });
      toast.success(lang === "es" ? "¡Gracias por tu reseña!" : "Thanks for your review!");
      setRating(0); setComment(""); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-12" data-testid="reviews-section">
      <h2 className="font-head text-xl font-bold text-secondary">{lang === "es" ? "Reseñas" : "Reviews"} ({reviews.length})</h2>

      {user && user.role ? (
        <form onSubmit={submit} className="mt-4 rounded-sm border border-slate-200 bg-white p-5" data-testid="review-form">
          <p className="text-sm font-semibold text-slate-600">{lang === "es" ? "Deja tu calificación" : "Leave your rating"}</p>
          <div className="mt-2"><Stars value={rating} onChange={setRating} /></div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder={lang === "es" ? "Cuéntanos tu experiencia (opcional)" : "Tell us about your experience (optional)"} className="mt-3 rounded-sm" data-testid="review-comment-input" />
          <Button type="submit" disabled={submitting} className="mt-3 rounded-sm bg-primary hover:bg-orange-600" data-testid="review-submit-btn">{lang === "es" ? "Publicar reseña" : "Post review"}</Button>
        </form>
      ) : (
        <p className="mt-3 rounded-sm border border-dashed border-slate-300 p-4 text-sm text-slate-500" data-testid="review-login-hint">{lang === "es" ? "Inicia sesión para dejar una reseña." : "Log in to leave a review."}</p>
      )}

      <div className="mt-6 space-y-4">
        {reviews.map((r) => (
          <div key={r.id} className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`review-item-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-head font-bold text-secondary">{r.user_name}</span>
              <Stars value={r.rating} size="h-4 w-4" />
            </div>
            {r.comment && <p className="mt-2 text-sm text-slate-600">{r.comment}</p>}
            <p className="mt-1 text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</p>
          </div>
        ))}
        {reviews.length === 0 && <p className="py-6 text-center text-sm text-slate-400">{lang === "es" ? "Aún no hay reseñas." : "No reviews yet."}</p>}
      </div>
    </div>
  );
}
